import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { JobApplication } from "./ApplicationWizard";
import {
  GuidedInterviewPrepRecord,
  jobApplicationService,
} from "../services/jobApplicationService";
import {
  getOutputModeLabel,
  getPrepTypeLabel,
} from "./interviewPrepOptions";
import "./StoredInterviewPrepsView.css";

interface StoredInterviewPrepsViewProps {
  application: JobApplication;
  onViewPrep: (prep: GuidedInterviewPrepRecord) => void;
}

export const StoredInterviewPrepsView: React.FC<StoredInterviewPrepsViewProps> = ({
  application,
  onViewPrep,
}) => {
  const [preps, setPreps] = useState<GuidedInterviewPrepRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadPreps = useCallback(async () => {
    if (!application.id) {
      setPreps([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const storedPreps = await jobApplicationService.getStoredInterviewPreps(
        application.id,
      );
      setPreps(storedPreps);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load interview preps",
      );
    } finally {
      setIsLoading(false);
    }
  }, [application.id]);

  useEffect(() => {
    loadPreps();
  }, [loadPreps]);

  const handleDeleteClick = (prepId: string) => {
    setDeleteConfirmId(prepId);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    setIsDeleting(true);
    try {
      await jobApplicationService.deleteStoredInterviewPrep(deleteConfirmId);
      toast.success("Interview prep deleted");
      setDeleteConfirmId(null);
      await loadPreps();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete interview prep");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="stored-interview-preps-state">
        <p>Loading saved interview preps...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stored-interview-preps-state stored-interview-preps-state-error">
        <p>{error}</p>
        <button type="button" onClick={loadPreps}>
          Retry
        </button>
      </div>
    );
  }

  if (preps.length === 0) {
    return (
      <div className="stored-interview-preps-state">
        <p>No saved interview preps for this job yet.</p>
      </div>
    );
  }

  return (
    <>
    <div className="stored-interview-preps-list">
      {preps.map((prep) => (
        <article
          key={prep.prep_id}
          className="stored-interview-prep-card"
          onClick={() => onViewPrep(prep)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onViewPrep(prep);
            }
          }}
        >
          <div className="stored-interview-prep-card-header">
            <div>
              <h4>{prep.title || "Interview Prep"}</h4>
              <p>{prep.short_description || "Saved interview preparation artifact"}</p>
            </div>
            <span
              className={`stored-interview-prep-status stored-interview-prep-status-${prep.status}`}
            >
              {prep.status}
            </span>
          </div>

          <div className="stored-interview-prep-meta">
            <span>{getPrepTypeLabel(prep.prep_type)}</span>
            <span>{getOutputModeLabel(prep.output_mode)}</span>
            {prep.created_at && (
              <span>{new Date(prep.created_at).toLocaleString()}</span>
            )}
          </div>

          <div className="stored-interview-prep-actions">
            <button type="button" onClick={() => onViewPrep(prep)}>
              View
            </button>
            <button
              type="button"
              className="stored-interview-prep-delete"
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteClick(prep.prep_id);
              }}
            >
              Delete
            </button>
          </div>
        </article>
      ))}
    </div>

    {deleteConfirmId && (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title">Delete Interview Prep</h3>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to delete this saved interview prep? This action cannot be undone.</p>
            {isDeleting && (
              <div className="delete-loading">
                <div className="loading-spinner small"></div>
                <span>Deleting...</span>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={handleDeleteCancel}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={() => void handleDeleteConfirm()}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="loading-spinner small"></div>
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};
